<script setup lang="ts">

import DistributorAvailabilityInfoWindow
    from "@/disruption-logistics/components/maps/distributors/DistributorAvailabilityInfoWindow.vue";
import DistributorAvailabilityMapMarker
    from "@/disruption-logistics/components/maps/distributors/DistributorAvailabilityMapMarker.vue";
import {InfoWindow} from "vue3-google-map";
import DisruptionLogisticsMap from "@/disruption-logistics/components/maps/DisruptionLogisticsMap.vue";
import {
    DistributorProductAvailability, HealthcenterGroupDistributorDetails,
    HealthcenterGroupMember
} from "@/disruption-logistics/interfaces/availability-providers";
import {nextTick, ref, watch} from "vue";
import {Warehouse} from "@/core/interfaces/warehouses";

const props = defineProps<{
    loading: boolean,
    disabled?: boolean,
    infoContent?: {
        warehouse: Warehouse,
        available: boolean | null,
        message: string | null,
        position: { lat: number, lng: number }
    },
    open: boolean,
    data?: HealthcenterGroupDistributorDetails[] | null,
    warehouses: Warehouse[]
}>();
defineEmits<{ (e: 'toggle-info-window', warehouse: Warehouse): void }>();

const infoWindowOpened = ref<boolean>(props.open);
const mapRef = ref<InstanceType<typeof DisruptionLogisticsMap>>();

/**
 * Fit the map to display all warehouses.
 */
const fitMapToWarehouses = () => {
    nextTick(() => {
        const bounds = props.warehouses.map(warehouse => ({
            lat: warehouse.geo_lat,
            lng: warehouse.geo_lon
        }));

        try {
            if (mapRef.value) {
                mapRef.value.fitMapToBounds(bounds, 0);
            }
        } catch (e) {
            console.error("ERRORR", e);
        }
    });
};

defineExpose({fitMapToWarehouses});

watch(() => props.infoContent, () => {
    infoWindowOpened.value = false;

    fitMapToWarehouses();
}, {immediate: true, deep: true});

/**
 * Get the availability for a warehouse.
 */
const warehouseAvailability = (warehouse: Warehouse): DistributorProductAvailability[] | undefined => {
    if (!props.data) {
        return undefined;
    }

    return props.data?.find(
        availability => availability.warehouse?.id === warehouse.id
    )?.products;
};

/**
 * Determine if the product is available in the warehouse.
 * It return null, if we doesn't have the availability for a warehouse
 *
 * @return {boolean|null}
 */
const isWarehouseAvailable = (warehouse: Warehouse): boolean | null => {
    const availabilities = warehouseAvailability(warehouse);

    if (!availabilities?.length) {
        return null;
    }

    return availabilities.some(product => product.isAvailable);
};


/**
 * Get the members for a warehouse.
 *
 * @param {Object} warehouse
 */
const warehouseMembers = (warehouse: Warehouse): HealthcenterGroupMember[] => {
    if (!props.data) {
        return [];
    }

    return props.data?.filter(datum => datum.warehouse.id === warehouse.id)
        .map(datum => datum.members)
        .flat();
};
</script>

<template>
  <disruption-logistics-map
    ref="mapRef"
    :loading-data="loading"
    :disabled="disabled"
  >
    <info-window
      v-if="infoContent"
      :v-model="infoWindowOpened"
      :options="{
                position: {
                  lat: infoContent?.position.lat as number,
                  lng: infoContent?.position.lng as number
                }
              }"
    >
      <distributor-availability-info-window
        :content="infoContent"
        :members="warehouseMembers(infoContent.warehouse)"
      />
    </info-window>
    <distributor-availability-map-marker
      v-for="warehouse in warehouses"
      :key="warehouse.id"
      :disabled="disabled"
      :warehouse="warehouse"
      :is-available="isWarehouseAvailable(warehouse)"
      :members="warehouseMembers(warehouse)"
      @click="$emit('toggle-info-window', $event)"
    />
  </disruption-logistics-map>
</template>

<style scoped>

</style>